import { useState } from "react";
import { MdOutlineError } from "react-icons/md";
import { ERROR_PANEL_DEFAULT_NOTE } from "../../../../../components/common/ErrorPanel/ErrorPanel";
import MeritoButton from "../../../../../components/common/MeritoButtons/MeritoButtons";
import MeritoPasswordField from "../../../../../components/common/MeritoPasswordField/MeritoPasswordField";
import TruncateTooltip from "../../../../../components/common/TruncateTooltip/TruncateTooltip";
import type { CoverView } from "../../../api/connectionHub.types";
import ConnectionLogo from "../../common/ConnectionLogo/ConnectionLogo";
import styles from "./PasswordRetryCard.module.scss";

export interface PasswordRetryCardProps {
  cover: CoverView;
  busy?: boolean;
  disabled?: boolean;
  onSaveAndRetry: (password: string) => Promise<boolean>;
}

const PasswordRetryCard = ({ cover, busy = false, disabled = false, onSaveAndRetry }: PasswordRetryCardProps): JSX.Element => {
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const fieldId = `retry-password-${cover.connectionId}`;

  const submit = async (): Promise<void> => {
    const value = password.trim();
    if (!value || saving || disabled) return;
    setSaving(true);
    const saved = await onSaveAndRetry(value);
    setSaving(false);
    if (saved) setPassword("");
  };

  return (
    <section className={styles.card} aria-label={`${cover.ipName} sign-in failed`}>
      <header className={styles.header}>
        <ConnectionLogo logoUrl={cover.logo} className={styles.logo} />
        <div className={styles.identity}>
          <TruncateTooltip text={cover.ipName} className={styles.name} />
          <p className={styles.error}>
            <MdOutlineError aria-hidden="true" className={styles.errorIcon} />
            Sign-in failed. The password was incorrect.
          </p>
        </div>
      </header>
      <div className={styles.body}>
        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={fieldId}>{cover.ipName} password</label>
            <MeritoPasswordField
              id={fieldId}
              value={password}
              onChange={setPassword}
              placeholder="Enter correct password"
              disabled={saving || disabled}
              onEnter={() => { void submit(); }}
            />
          </div>
          <MeritoButton
            type="primary-1"
            size="md"
            className={styles.action}
            btnText="Save & Retry"
            loading={saving || busy}
            showLoadingWithText
            disabled={!password.trim() || disabled}
            onClick={() => { void submit(); }}
          />
        </div>
        <span className={styles.divider} />
        <p className={styles.note}>{ERROR_PANEL_DEFAULT_NOTE}</p>
      </div>
    </section>
  );
};

export default PasswordRetryCard;
